import { CONTRACT_SCHEMA_VERSION, contractSchemaVersionOf, deserialiseContract, type ObjectTypeDefinition } from "daedalus-core";
import { ODYSSEY_ADAPTER } from "../editor/adapter.ts";
import { assertVerbNames } from "./verbs.ts";

/**
 * The object types a world version runs with (ADR-052, ADR-058), which client and map server both read from the
 * version's game contract so the verb one offers is the transition the other applies.
 */

/**
 * Where a world version's object types came from: its contract, the built-in adapter because the version
 * carries none, or the built-in adapter because its contract was written to an older layout than this
 * build reads.
 */
export type ObjectTypesSource = "contract" | "built-in" | "outdated";

/**
 * A world version's object types by type id, and where they came from.
 */
export interface GameObjectTypes {
  readonly types: ReadonlyMap<string, ObjectTypeDefinition>;
  readonly source: ObjectTypesSource;
}

/**
 * The object types of a world version from its contract text. A version compiled before contracts, or
 * whose contract predates transitions as data, runs with the built-in adapter's types, which are the ones
 * the build that compiled it answered with; the same rule the editor applies to a served contract. A
 * contract of this build's layout is authoritative and must be well formed.
 *
 * @param contractText The version's `contract.json`, or null when it has none.
 * @returns The types by id and their source.
 * @throws SyntaxError when the text is not JSON; Error when the contract is of a layout newer than this build
 *   reads, is malformed, or declares a verb the protocol cannot carry.
 */
export function gameObjectTypes(contractText: string | null): GameObjectTypes {
  if (contractText === null) {
    return { types: tableOf(ODYSSEY_ADAPTER.objectTypes ?? []), source: "built-in" };
  }
  const version = contractSchemaVersionOf(contractText);
  if (version !== null && version < CONTRACT_SCHEMA_VERSION) {
    return { types: tableOf(ODYSSEY_ADAPTER.objectTypes ?? []), source: "outdated" };
  }
  const types = deserialiseContract(contractText).objectTypes;
  for (const type of types) {
    assertVerbNames(type);
  }
  return { types: tableOf(types), source: "contract" };
}

/**
 * Object types keyed by id.
 *
 * @param types The types.
 * @returns The table.
 */
function tableOf(types: readonly ObjectTypeDefinition[]): ReadonlyMap<string, ObjectTypeDefinition> {
  return new Map(types.map((type) => [type.type, type]));
}
